/**
 * Unified Tracking Manager
 * Consolidates all tracking functionality into a single optimized system
 */

import { supabase } from "@/integrations/supabase/client";
import {
  getDeviceInfo,
  getDeviceHash,
  getSessionId,
  getUTMParams,
  updateViewport,
} from "./deviceDetection";

// Types
interface BaseEvent {
  type: string;
  timestamp: number;
  sessionId: string;
  deviceHash: string;
  path: string;
  pageUrl: string;
  pageTitle: string;
  metadata?: Record<string, any>;
}

interface PageViewEvent extends BaseEvent {
  type: 'pageview';
  landing: boolean;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  viewportWidth: number;
  viewportHeight: number;
  language: string;
  timezone: string;
  userAgent: string;
  lcp?: number;
  cls?: number;
  inp?: number;
  fcp?: number;
  ttfb?: number;
}

interface ClickEvent extends BaseEvent {
  type: 'click' | 'contact_click' | 'social_share';
  element?: string;
  clickType?: string;
  platform?: string;
  listingId?: string;
}

interface CustomEvent extends BaseEvent {
  type: 'event' | 'engagement' | 'scroll' | 'affiliate_click' | 'experiment';
  eventName?: string;
  engagementTimeMs?: number;
  scrollDepth?: number;
}

type TrackingEvent = PageViewEvent | ClickEvent | CustomEvent;

interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

/**
 * TrackingManager class - singleton pattern
 */
class TrackingManager {
  private static instance: TrackingManager;
  private sessionId: string;
  private deviceHash: string;
  private queue: TrackingEvent[] = [];
  private flushInterval = 5000;
  private flushTimer?: ReturnType<typeof setTimeout>;
  private engagementStart: number = Date.now();
  private isInitialized = false;
  private maxScrollDepth = 0;
  private isFirstPageView = true;

  private constructor() {
    this.sessionId = getSessionId();
    this.deviceHash = getDeviceHash();
  }

  public static getInstance(): TrackingManager {
    if (!TrackingManager.instance) {
      TrackingManager.instance = new TrackingManager();
    }
    return TrackingManager.instance;
  }

  /**
   * Check if analytics consent has been given
   */
  private hasConsent(category: keyof ConsentPreferences = 'analytics'): boolean {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) return false;

    try {
      const preferences: ConsentPreferences = JSON.parse(consent);
      return preferences[category] === true;
    } catch {
      return false;
    }
  }

  /**
   * Check if current page should be tracked
   */
  private shouldTrack(): boolean {
    // Skip if no consent
    if (!this.hasConsent('analytics')) return false;

    // Skip admin pages
    if (window.location.pathname.startsWith('/admin')) return false;

    // Skip Lovable preview/editor
    const hostname = window.location.hostname;
    if (hostname.includes('lovableproject.com') || hostname.includes('lovable.app')) {
      return false;
    }

    return true;
  }

  /**
   * Create base event object
   */
  private createBaseEvent(type: string, metadata?: Record<string, any>): BaseEvent {
    return {
      type,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      deviceHash: this.deviceHash,
      path: window.location.pathname,
      pageUrl: window.location.href,
      pageTitle: document.title,
      metadata,
    };
  }

  /**
   * Add event to queue and schedule flush
   */
  private enqueue(event: TrackingEvent): void {
    this.queue.push(event);
    this.scheduleFlush();
  }

  /**
   * Schedule flush with debounce
   */
  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
    this.flushTimer = setTimeout(() => this.flush(), this.flushInterval);
  }

  /**
   * Flush all queued events to the backend
   */
  private async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    try {
      const { error } = await supabase.functions.invoke('track-events', {
        body: { events },
      });

      if (error) {
        console.error('[TrackingManager] Flush error:', error);
        // Re-queue events on error (with limit to prevent memory issues)
        if (this.queue.length < 100) {
          this.queue.unshift(...events);
        }
      }
    } catch (error) {
      console.error('[TrackingManager] Flush error:', error);
      if (this.queue.length < 100) {
        this.queue.unshift(...events);
      }
    }
  }

  /**
   * Get Web Vitals metrics
   */
  private async getWebVitals(): Promise<{
    lcp?: number;
    cls?: number;
    inp?: number;
    fcp?: number;
    ttfb?: number;
  }> {
    return new Promise((resolve) => {
      const vitals: Record<string, number> = {};

      if ('PerformanceObserver' in window) {
        // LCP
        try {
          const lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1] as any;
            vitals.lcp = lastEntry.renderTime || lastEntry.loadTime;
          });
          lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
        } catch {}

        // CLS
        try {
          let clsValue = 0;
          const clsObserver = new PerformanceObserver((list) => {
            for (const entry of list.getEntries() as any[]) {
              if (!entry.hadRecentInput) {
                clsValue += entry.value;
              }
            }
            vitals.cls = clsValue;
          });
          clsObserver.observe({ type: 'layout-shift', buffered: true });
        } catch {}

        // FCP
        try {
          const fcpObserver = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.name === 'first-contentful-paint') {
                vitals.fcp = entry.startTime;
              }
            }
          });
          fcpObserver.observe({ type: 'paint', buffered: true });
        } catch {}
      }

      // TTFB
      try {
        const navEntry = performance.getEntriesByType('navigation')[0] as any;
        if (navEntry) {
          vitals.ttfb = navEntry.responseStart - navEntry.requestStart;
        }
      } catch {}

      // Give observers time to collect data
      setTimeout(() => resolve(vitals), 500);
    });
  }

  /**
   * Initialize tracking - sets up event listeners
   */
  public async init(): Promise<void> {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Track initial page view
    await this.trackPageView();

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackEngagement();
        this.flush();
      } else {
        this.engagementStart = Date.now();
      }
    });

    // Track before unload - use sendBeacon for reliability
    window.addEventListener('beforeunload', () => {
      this.trackEngagement();
      // Use sendBeacon for unload
      if (this.queue.length > 0 && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify({ events: this.queue })], {
          type: 'application/json',
        });
        navigator.sendBeacon(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-events`,
          blob
        );
        this.queue = [];
      } else {
        this.flush();
      }
    });

    // Track scroll depth (debounced)
    let scrollTimeout: ReturnType<typeof setTimeout>;
    window.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => this.trackScrollDepth(), 150);
      },
      { passive: true }
    );

    // Track external link clicks
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      if (link && link.href && !link.href.startsWith(window.location.origin)) {
        this.trackClick('external_link', { url: link.href });
      }
    });

    // Update viewport on resize
    window.addEventListener('resize', () => {
      updateViewport();
    }, { passive: true });
  }

  /**
   * Track page view
   */
  public async trackPageView(): Promise<void> {
    if (!this.shouldTrack()) return;

    const deviceInfo = getDeviceInfo();
    const utmParams = getUTMParams();
    const webVitals = this.isFirstPageView ? await this.getWebVitals() : {};

    const event: PageViewEvent = {
      ...this.createBaseEvent('pageview'),
      type: 'pageview',
      landing: this.isFirstPageView,
      referrer: document.referrer,
      ...utmParams,
      viewportWidth: deviceInfo.viewport.width,
      viewportHeight: deviceInfo.viewport.height,
      language: deviceInfo.language,
      timezone: deviceInfo.timezone,
      userAgent: navigator.userAgent,
      ...webVitals,
    };

    this.enqueue(event);
    this.engagementStart = Date.now();
    this.maxScrollDepth = 0;
    this.isFirstPageView = false;
  }

  /**
   * Track custom event
   */
  public trackEvent(eventName: string, metadata?: Record<string, any>): void {
    if (!this.shouldTrack()) return;

    const event: CustomEvent = {
      ...this.createBaseEvent('event', { eventName, ...metadata }),
      type: 'event',
      eventName,
    };

    this.enqueue(event);
  }

  /**
   * Track click
   */
  public trackClick(element: string, metadata?: Record<string, any>): void {
    if (!this.shouldTrack()) return;

    const event: ClickEvent = {
      ...this.createBaseEvent('click', { element, ...metadata }),
      type: 'click',
      element,
    };

    this.enqueue(event);
  }

  /**
   * Track contact click (phone, email, website)
   */
  public async trackContactClick(
    listingId: string,
    clickType: 'email' | 'phone' | 'website'
  ): Promise<void> {
    // Direct database insert for contact clicks (immediate, not batched)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from('listing_contact_clicks').insert({
        listing_id: listingId,
        click_type: clickType,
        user_id: user?.id || null,
      });
    } catch (error) {
      console.error('[TrackingManager] Contact click error:', error);
    }

    // Also queue for analytics
    if (this.shouldTrack()) {
      const event: ClickEvent = {
        ...this.createBaseEvent('contact_click', { listingId, clickType }),
        type: 'contact_click',
        clickType,
        listingId,
      };
      this.enqueue(event);
    }
  }

  /**
   * Track social share
   */
  public async trackSocialShare(
    listingId: string,
    platform: 'facebook' | 'twitter' | 'linkedin' | 'whatsapp' | 'email' | 'copy'
  ): Promise<void> {
    // Direct database insert
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from('listing_social_shares').insert({
        listing_id: listingId,
        platform,
        user_id: user?.id || null,
      });
    } catch (error) {
      console.error('[TrackingManager] Social share error:', error);
    }

    // Also queue for analytics
    if (this.shouldTrack()) {
      const event: ClickEvent = {
        ...this.createBaseEvent('social_share', { listingId, platform }),
        type: 'social_share',
        platform,
        listingId,
      };
      this.enqueue(event);
    }
  }

  /**
   * Track engagement time
   */
  private trackEngagement(): void {
    if (!this.shouldTrack()) return;

    const engagementTimeMs = Date.now() - this.engagementStart;

    const event: CustomEvent = {
      ...this.createBaseEvent('engagement', { engagementTimeMs }),
      type: 'engagement',
      engagementTimeMs,
    };

    this.enqueue(event);
  }

  /**
   * Track scroll depth
   */
  private trackScrollDepth(): void {
    if (!this.shouldTrack()) return;

    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) return;

    const scrollPercentage = Math.round((window.scrollY / scrollHeight) * 100);

    // Only track milestones (25%, 50%, 75%, 100%)
    const milestones = [25, 50, 75, 100];
    for (const milestone of milestones) {
      if (scrollPercentage >= milestone && this.maxScrollDepth < milestone) {
        this.maxScrollDepth = milestone;
        this.trackEvent(`scroll_${milestone}`, { depth: milestone });
        break;
      }
    }
  }

  /**
   * Track affiliate click
   */
  public async trackAffiliateClick(refCode: string, affiliateUserId: string): Promise<void> {
    const deviceInfo = getDeviceInfo();

    try {
      await supabase.from('affiliate_clicks').insert({
        affiliate_user_id: affiliateUserId,
        ref_code: refCode,
        referrer_url: document.referrer || null,
        landing_page: window.location.href,
        device_hash: this.deviceHash,
        user_agent: navigator.userAgent,
        device_type: deviceInfo.type,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        session_id: this.sessionId,
      });
    } catch (error) {
      console.error('[TrackingManager] Affiliate click error:', error);
    }
  }

  /**
   * Get session ID (for external use)
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get device hash (for external use)
   */
  public getDeviceHash(): string {
    return this.deviceHash;
  }

  /**
   * Force flush (for critical events)
   */
  public async forceFlush(): Promise<void> {
    await this.flush();
  }
}

// Export singleton instance
export const trackingManager = TrackingManager.getInstance();

// Export convenience functions for backward compatibility
export const initTracking = () => trackingManager.init();
export const trackPageView = () => trackingManager.trackPageView();
export const trackEvent = (name: string, metadata?: Record<string, any>) =>
  trackingManager.trackEvent(name, metadata);
export const trackClick = (element: string, metadata?: Record<string, any>) =>
  trackingManager.trackClick(element, metadata);
export const trackContactClick = (listingId: string, clickType: 'email' | 'phone' | 'website') =>
  trackingManager.trackContactClick(listingId, clickType);
export const trackSocialShare = (
  listingId: string,
  platform: 'facebook' | 'twitter' | 'linkedin' | 'whatsapp' | 'email' | 'copy'
) => trackingManager.trackSocialShare(listingId, platform);
